import { LMEUserSettings } from "@mcleod/general";
import {
    AutogenLayoutOfficePayrollProcessingMultiProfile
} from "./autogen/AutogenLayoutOfficePayrollProcessingMultiProfile";
import { Api, Model, ModelRow, SettingsLoader } from "@mcleod/core";
import { Router, UserLayoutProvider } from "@mcleod/common";
import { MultiProfilePlanningTabs } from "@mcleod/dispatch/src/planning/MultiProfilePlanningTabs";
import {
    Component, DataSource, DataSourceRemoteDataChangeEvent, PanelProps, SelectionEvent, Snackbar, Tab
} from "@mcleod/components";
import { OfficePayrollProcessing } from "./OfficePayrollProcessing";

export class OfficePayrollProcessingMultiProfile extends AutogenLayoutOfficePayrollProcessingMultiProfile {
    private userProfiles: string[] = [];
    private payrollControl: ModelRow;

    async onLoad(): Promise<void> {
        if (LMEUserSettings.get()?.office_payroll_profile != null)
            this.userProfiles.push(LMEUserSettings.get().office_payroll_profile);
        if (LMEUserSettings.get()?.office_payroll_profile2 != null)
            this.userProfiles.push(LMEUserSettings.get().office_payroll_profile2);
        if (LMEUserSettings.get()?.office_payroll_profile3 != null)
            this.userProfiles.push(LMEUserSettings.get().office_payroll_profile3);
        if (LMEUserSettings.get()?.office_payroll_profile4 != null)
            this.userProfiles.push(LMEUserSettings.get().office_payroll_profile4);

        new MultiProfilePlanningTabs({
            tabset: this.tabsetOfficePayrollProfiles,
            profileDataSource: this.sourceOfficePayrollProfile,
            mainLayoutName: "lme/backoffice/officepayroll/OfficePayrollProcessing",
            advancedFilterLayout: "lme/backoffice/officepayroll/OfficePayrollProfileFilter",
            startingProfiles: this.userProfiles,
            profileDescrFieldName: "descr",
            userSettingsCallback: () => UserLayoutProvider.navigateTo(null, null, { scrollTo: "tabBackOffice" })
        });
        this.sourcePayeeCache.search({ is_dummy: true }).then(() => this.sourcePayeeCache.clear());
        this.sourceOrderPayablesCache.search({ is_dummy: true }).then(() => this.sourceOrderPayablesCache.clear());
        this.sourceStateFederalTaxDriverSettlement.search().then(() => this.sourceStateFederalTaxDriverSettlement.clear());
        this.sourceVoidedPayablesOfficePayroll.search().then(() => this.sourceVoidedPayablesOfficePayroll.clear());
    }

    handleRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        event.preventDefault();
        this.tabsetOfficePayrollProfiles.tabs.forEach((tab: Tab) => {
            tab.components.forEach((component: Component) => {
                if (component instanceof OfficePayrollProcessing) {
                    if ("lme/backoffice/officepayroll/order-payables-cache" === (event.target as DataSource).url) {
                        const dataSource = component.sourceOrderPayablesCache;
                        dataSource.handleDataChange(event.change);
                    } else if ("lme/backoffice/officepayroll/payee-cache" === (event.target as DataSource).url) {
                        const dataSource = component.sourcePayeeCache;
                        dataSource.handleDataChange(event.change);
                    } else if ("lme/settlement/driver-settlement/state-federal-tax-driver-settlement" === (event.target as DataSource).url) {
                        const dataSource = component.sourceStateFederalTaxDriverSettlement;
                        dataSource.handleDataChange(event.change);
                    } else if ("lme/backoffice/officepayroll/voided-payables-office-payroll" === (event.target as DataSource).url) {
                        const dataSource = component.sourceVoidedPayablesOfficePayroll;
                        dataSource.handleDataChange(event.change);
                    }
                }
            });
        });
    }

    // Override the main router's padding to get the vertical profile tabset to be flush with the edge of the screen
    getRouterProps(): Partial<PanelProps> {
        return { padding: 0 };
    }

    tabsetOfficePayrollProfilesAfterTabSelection(event: SelectionEvent) {
        if (event.oldSelection) {
            const oldTab = event.oldSelection as Tab;
            const officePayrollProcessingLayout = oldTab.components?.find((comp: Component) => comp instanceof OfficePayrollProcessing) as OfficePayrollProcessing;
            if (officePayrollProcessingLayout != null && officePayrollProcessingLayout.profileRow != null) {
                Router.instance.dismissAllPopups();
            }
        }
        if (event.newSelection) {
            const tab = event.newSelection as Tab;
            const officePayrollProcessingLayout = tab.components?.find((comp: Component) => comp instanceof OfficePayrollProcessing) as OfficePayrollProcessing;
            if (officePayrollProcessingLayout != null && officePayrollProcessingLayout.profileRow != null) {
                if (officePayrollProcessingLayout.snackbars.size > 0) {
                    officePayrollProcessingLayout.snackbars.forEach((snackbar) => {
                        Snackbar.showSnackbar(snackbar, { persist: true });
                    });
                } else {
                    officePayrollProcessingLayout.checkBonusTaxRatesSetup();
                }
                officePayrollProcessingLayout.setCutoffDates();
            }
            if (officePayrollProcessingLayout != null) {
                officePayrollProcessingLayout.updateCutoffDate = this.updateCutoffDate.bind(this);
            }
        }
    }

    async updateCutoffDate(): Promise<ModelRow | null> {
        const response = await Api.post("lme/settlement/update-cutoff-date", {
            is_office: true
        });
        if (response?.data?.[0]?.cutoff_date) {
            this.payrollControl = await Model.searchSingleRecord("lme/dispatch/payroll-control");
            this.tabsetOfficePayrollProfiles.components.forEach((tab: Tab) => {
                tab.components.forEach((component: Component) => {
                    if (component instanceof OfficePayrollProcessing) {
                        component.payrollControl = this.payrollControl;
                    }
                });
            });
            // reload auth settings to get the new cutoff date
            SettingsLoader.loadAuthSettings(null, null, null);
            return this.payrollControl;
        }
        return null;
    }
}
